import React from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import type { EventItem } from '../../types/shared/event';
import { logger } from '../../lib/logger';
import { generateGoogleCalendarUrl, generateAppleCalendarUrl, generateOutlookCalendarUrl } from '../../utils/calendarUtils';

interface CalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: EventItem;
  config: any;
  translations: Record<string, Record<string, string>>;
  description: string;
}

// Google Calendar Icon SVG
const GoogleCalendarIcon = () => (
  <svg viewBox="0 0 512 512" width="28" height="28">
    <g>
      <path id="Path_15_" d="m176.539 330.307c-10.072-6.804-17.044-16.741-20.851-29.878l23.377-9.634c2.122 8.084 5.827 14.349 11.116 18.796 5.255 4.446 11.655 6.636 19.133 6.636 7.646 0 14.215-2.324 19.705-6.973 5.491-4.648 8.253-10.577 8.253-17.752 0-7.343-2.897-13.339-8.691-17.987s-13.069-6.973-21.76-6.973h-13.507v-23.141h12.126c7.478 0 13.777-2.021 18.897-6.063s7.68-9.566 7.68-16.606c0-6.265-2.291-11.251-6.872-14.989-4.581-3.739-10.375-5.625-17.415-5.625-6.872 0-12.328 1.819-16.371 5.491-4.042 3.672-6.973 8.185-8.825 13.507l-23.141-9.634c3.065-8.691 8.691-16.371 16.943-23.006 8.253-6.636 18.796-9.971 31.596-9.971 9.465 0 17.987 1.819 25.533 5.491 7.545 3.672 13.474 8.758 17.752 15.225 4.278 6.501 6.4 13.777 6.4 21.861 0 8.253-1.987 15.225-5.962 20.952-3.975 5.726-8.859 10.105-14.653 13.171v1.381c7.646 3.2 13.878 8.084 18.796 14.653 4.884 6.568 7.343 14.417 7.343 23.579s-2.324 17.347-6.973 24.522c-4.648 7.175-11.082 12.834-19.234 16.943-8.185 4.109-17.381 6.198-27.587 6.198-11.823.033-22.736-3.369-32.808-10.174z" fill="#0085f7"/>
      <path id="Path_14_" d="m320.135 214.299-25.668 18.56-12.833-19.47 46.046-33.212h17.651v156.665h-25.196z" fill="#0085f7"/>
      <path id="Path_3_" d="m390.737 390.737h-269.474l-38.574 56.837 38.574 64.426h269.474l31.868-68.546z" fill="#00a94b"/>
      <path id="Path_4_" d="m390.737 0h-350.316c-22.333 0-40.421 18.088-40.421 40.421v350.316l60.632 43.103 60.632-43.103v-269.474h269.474l41.482-60.632z" fill="#0085f7"/>
      <path id="Path_5_" d="m0 390.737v80.842c0 22.333 18.088 40.421 40.421 40.421h80.842v-121.263z" fill="#00802e"/>
      <path id="Path_6_" d="m512 121.263-60.632-39.014-60.631 39.014v269.474l54.529 28.463 66.734-28.463z" fill="#ffbc00"/>
      <path id="Path_2_" d="m512 121.263v-80.842c0-22.333-18.088-40.421-40.421-40.421h-80.842v121.263z" fill="#0067d5"/>
      <path id="Path_1_" d="m390.737 512 121.263-121.263h-121.263z" fill="#ff4131"/>
    </g>
  </svg>
);

// Apple Calendar Icon SVG
const AppleCalendarIcon = () => (
  <svg viewBox="0 0 512 512" width="28" height="28">
    <g style={{opacity: 0.08}}>
      <path d="M129.051,512c-74.5,0-124.56-50.059-124.56-124.56V151.507c0-74.5,50.059-124.56,124.56-124.56h253.898
        c74.5,0,124.56,50.059,124.56,124.56V387.44c0,74.5-50.059,124.56-124.56,124.56H129.051z"/>
    </g>
    <linearGradient id="SVGID_1_" gradientUnits="userSpaceOnUse" x1="-15.0449" y1="581.5254" x2="-15.0449" y2="635.5254" gradientTransform="matrix(8.9825 0 0 -8.9825 391.1404 5726.5439)">
      <stop offset="0" style={{stopColor: "#ECECEC"}}/>
      <stop offset="1" style={{stopColor: "#FFFFFF"}}/>
    </linearGradient>
    <path style={{fill: "url(#SVGID_1_)"}} d="M129.051,503.018c-74.5,0-124.56-50.059-124.56-124.56V142.525
      c0-74.5,50.059-124.56,124.56-124.56h253.898c74.5,0,124.56,50.059,124.56,124.56v235.933c0,74.5-50.059,124.56-124.56,124.56
      H129.051z"/>
    <path style={{fill: "#F4413D"}} d="M4.491,143.719v-1.195c0-74.5,50.059-124.56,124.56-124.56h253.898
      c74.5,0,124.56,50.059,124.56,124.56v1.195H4.491z"/>
    <g style={{opacity: 0.16}}>
      <linearGradient id="SVGID_2_" gradientUnits="userSpaceOnUse" x1="-15.0449" y1="581.5254" x2="-15.0449" y2="635.5254" gradientTransform="matrix(8.9825 0 0 -8.9825 391.1404 5726.5439)">
        <stop offset="0" style={{stopColor: "#000000"}}/>
        <stop offset="0.06" style={{stopColor: "#000000", stopOpacity: 0}}/>
      </linearGradient>
      <path style={{fill: "url(#SVGID_2_)"}} d="M382.949,17.965H129.051c-74.5,0-124.56,50.059-124.56,124.56v235.933
        c0,74.5,50.059,124.56,124.56,124.56h253.898c74.5,0,124.56-50.059,124.56-124.56V142.525
        C507.509,68.024,457.45,17.965,382.949,17.965z M489.544,378.458c0,64.755-41.84,106.595-106.595,106.595H129.051
        c-64.755,0-106.595-41.84-106.595-106.595V142.525c0-64.755,41.84-106.595,106.595-106.595h253.898
        c64.755,0,106.595,41.84,106.595,106.595V378.458z"/>
    </g>
    <g>
      <path style={{fill: "#413D3D"}} d="M180.332,295.397h20.633c23.211,0,39.172-14.246,39.172-34.017c0-19.16-14.489-32.669-38.436-32.669
        c-21.98,0-37.583,13.267-39.424,32.912h-16.213c2.084-28.492,24.441-47.652,56.365-47.652c31.196,0,54.649,19.034,54.649,45.316
        c0,22.232-14.372,38.193-36.963,42.244v0.494c27.136,1.599,44.58,18.423,44.58,43.475c0,29.965-26.768,51.335-61.278,51.335
        c-35.741,0-59.931-20.39-61.036-48.757h16.213c1.347,19.77,19.16,34.017,44.706,34.017c25.663,0,44.337-15.108,44.337-36.109
        c0-22.6-17.435-36.352-45.927-36.352h-21.369C180.332,309.643,180.332,295.397,180.332,295.397z"/>
      <path style={{fill: "#413D3D"}} d="M335.441,234.604h-0.494c-2.82,1.716-33.522,23.947-47.526,32.669v-17.929
        c5.282-3.315,42.855-29.84,47.769-32.786h16.456v177.457h-16.213L335.441,234.604L335.441,234.604z"/>
    </g>
    <circle style={{opacity: 0.24}} cx="121.263" cy="80.842" r="26.947"/>
    <circle style={{opacity: 0.24}} cx="390.737" cy="80.842" r="26.947"/>
    <g style={{opacity: 0.16}}>
      <path d="M382.949,17.965H129.051c-74.5,0-124.56,50.059-124.56,124.56v235.933c0,74.5,50.059,124.56,124.56,124.56h253.898
        c74.5,0,124.56-50.059,124.56-124.56V142.525C507.509,68.024,457.45,17.965,382.949,17.965z M498.526,378.458
        c0,69.129-46.448,115.577-115.577,115.577H129.051c-69.129,0-115.577-46.448-115.577-115.577V142.525
        c0-69.129,46.448-115.577,115.577-115.577h253.898c69.129,0,115.577,46.448,115.577,115.577V378.458z"/>
    </g>
    <g>
      <linearGradient id="SVGID_3_" gradientUnits="userSpaceOnUse" x1="-30.0449" y1="627.5254" x2="-30.0449" y2="637.5254" gradientTransform="matrix(8.9825 0 0 -8.9825 391.1404 5726.5439)">
        <stop offset="0" style={{stopColor: "#FFFFFF"}}/>
        <stop offset="1" style={{stopColor: "#DCDCDC"}}/>
      </linearGradient>
      <path style={{fill: "url(#SVGID_3_)"}} d="M121.263,89.825c-4.958,0-8.982-4.024-8.982-8.982V8.982c0-4.958,4.024-8.982,8.982-8.982
        c4.958,0,8.982,4.024,8.982,8.982v71.86C130.246,85.8,126.221,89.825,121.263,89.825z"/>
      
      <linearGradient id="SVGID_4_" gradientUnits="userSpaceOnUse" x1="-0.0449" y1="627.5254" x2="-0.0449" y2="637.5254" gradientTransform="matrix(8.9825 0 0 -8.9825 391.1404 5726.5439)">
        <stop offset="0" style={{stopColor: "#FFFFFF"}}/>
        <stop offset="1" style={{stopColor: "#DCDCDC"}}/>
      </linearGradient>
      <path style={{fill: "url(#SVGID_4_)"}} d="M390.737,89.825c-4.958,0-8.982-4.024-8.982-8.982V8.982c0-4.958,4.024-8.982,8.982-8.982
        c4.958,0,8.982,4.024,8.982,8.982v71.86C399.719,85.8,395.695,89.825,390.737,89.825z"/>
    </g>
  </svg>
);

// Outlook Calendar Icon SVG
const OutlookCalendarIcon = () => (
  <svg viewBox="0 0 512 512" width="28" height="28">
    <g>
      <path style={{fill: "#0072c6"}} d="M312.7,223.5v-102.9h172.9s0,0,0,0c4.8,0,11.4,3.3,16.1,8.8,3.7,4.3,6.4,10,6.4,16.6v2.8l-2.4,1.6-146.8,99-1.4.7c-1,.3-2.3,.5-3.4,.5s-2.4-.2-3.4-.4l-1.4-.6-34.1-21.5-2.5-1.6v-2.9h0Z"/>
      <path style={{fill: "#0072c6"}} d="M321.1,241.9l31.1,21.4h.1c1.3,1.1,3.3,0,3.7-.1l143.7-95.7,8.3-5.5v189.4c0,10.6-3.3,18.4-8.5,23.9-6,6.3-14.5,9.2-23.4,9.2h-163.5v-5.4s0-132.6,0-132.6v-5.4h5.4l3,.9h0Z"/>
      <path style={{fill: "#0072c6"}} d="M157.1,195c-5.4,0-10.1,1.2-14.3,3.7-4.3,2.5-8.1,6.4-11.4,11.7h0c-3.4,5.5-6.1,12-7.8,19.5-1.8,7.6-2.7,16.3-2.7,25.9s.9,18.5,2.7,26.1c1.8,7.4,4.4,13.8,7.8,19.1,3.3,5.1,7,8.9,11.2,11.4,4,2.4,8.6,3.5,13.6,3.5h0c5.3,0,10-1.1,14.1-3.5,4.2-2.4,7.9-6,11.1-10.9,3.4-5.2,5.9-11.4,7.6-18.8,1.8-7.6,2.7-16.3,2.7-26.1s-.9-19.4-2.6-27.3c-1.7-7.7-4.2-14.2-7.4-19.5-3.1-5.1-6.7-8.8-10.8-11.2-4-2.4-8.5-3.6-13.7-3.6h0s0,0,0,0Z"/>
      <path style={{fill: "#0072c6", fillRule: "evenodd"}} d="M305.1,505.4V0l-6.4,1.2L8.3,57.5l-4.4.8v390.6l4.3.9,290.4,60.9,6.5,1.4v-6.6h0ZM157.8,163.4c10.4,0,19.9,2.1,28.4,6.4,8.5,4.3,15.9,10.7,22.3,19.2h0c6.3,8.4,11.1,18.1,14.2,29,3.1,10.8,4.7,22.9,4.7,36.3h0c0,13.7-1.6,26.1-4.8,37.2-3.3,11.3-8.2,21.2-14.7,29.9-6.6,8.7-14.3,15.3-23.1,19.7-8.8,4.4-18.5,6.6-29.2,6.6h0c-10.5,0-20-2.2-28.6-6.4-8.6-4.3-16.1-10.7-22.7-19.1-6.5-8.4-11.3-18-14.5-28.8-3.2-10.7-4.8-22.6-4.8-35.7s1.6-26.3,4.8-37.5c3.3-11.3,8.2-21.4,14.7-30.1,6.7-8.9,14.4-15.6,23.3-20,8.9-4.5,18.9-6.7,29.9-6.7h0s0,0,0,0Z"/>
    </g>
  </svg>
);

export const CalendarModal: React.FC<CalendarModalProps> = ({
  isOpen,
  onClose,
  event,
  config,
  translations,
  description
}) => {
  const { i18n } = useTranslation();

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getTranslatedText = (key: string): string => {
    const translation = translations[key];
    if (!translation) return key;
    return translation[i18n.language] || translation.fr || key;
  };

  // Create a calendar event object
  const createCalendarEvent = (): { startDate: Date; endDate: Date } => {
    try {
      // Ensure we have date and time
      if (!event.date || !event.hour) {
        logger.warn('Missing date or time for calendar event', {
          category: 'Calendar',
          data: { 
            eventId: event.id,
            eventName: event.name,
            date: event.date,
            hour: event.hour
          }
        });
        throw new Error('Missing date or time information');
      }

      // Parse date and time
      const [year, month, day] = event.date.split('-').map(Number);
      const [hour, minute] = event.hour.split(':').map(Number);
      
      // Create start date
      const startDate = new Date(year, month - 1, day, hour, minute);
      
      // Create end date based on available information
      let endDate: Date;
      
      if (event.end_time) {
        // If end_time is available, use it
        const [endHour, endMinute] = event.end_time.split(':').map(Number);
        endDate = new Date(year, month - 1, day, endHour, endMinute);
      } else if (event.duration) {
        // If duration is available, add it to start time
        endDate = new Date(startDate);
        endDate.setMinutes(endDate.getMinutes() + Number(event.duration));
      } else {
        // Default to 1 hour duration
        endDate = new Date(startDate);
        endDate.setHours(endDate.getHours() + 1);
      }

      return { startDate, endDate };
    } catch (error) {
      logger.error('Error creating calendar event', {
        category: 'Calendar',
        data: { error, event }
      });
      throw error;
    }
  };

  // Handle calendar button click
  const handleCalendarClick = (type: 'google' | 'apple' | 'outlook') => {
    try {
      const { startDate, endDate } = createCalendarEvent();
      
      // Create calendar event object
      const calendarEvent = {
        title: event.name,
        description: description || event.description || '',
        location: event.fulladress || event.place || '',
        startDate,
        endDate
      };
      
      // Log the action
      logger.debug('Calendar option selected', {
        category: 'Calendar',
        data: { 
          type,
          eventId: event.id,
          eventName: event.name,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        }
      });
      
      // Generate URL based on calendar type
      let url = '#';
      switch (type) {
        case 'google':
          url = generateGoogleCalendarUrl([calendarEvent]);
          break;
        case 'apple':
          url = generateAppleCalendarUrl([calendarEvent]);
          break;
        case 'outlook':
          url = generateOutlookCalendarUrl([calendarEvent]);
          break;
      }
      
      // Open the URL in a new tab
      if (url !== '#') {
        window.open(url, '_blank');
      }
      
      // Close the modal
      onClose();
    } catch (error) {
      logger.error('Error handling calendar selection', {
        category: 'Calendar',
        data: { error, type, eventId: event.id }
      });
      alert(getTranslatedText('calendar_error') || 'Error adding to calendar');
      onClose();
    }
  };

  return (
    <div 
      className="fixed bottom-20 right-4 z-[500]"
      onClick={handleOverlayClick}
    >
      <div 
        className="bg-white rounded-lg shadow-lg overflow-hidden"
        style={{ borderRadius: config.border_radius }}
        onClick={e => e.stopPropagation()}
      >
        <div className="p-3 space-y-2">
          <div className="flex justify-between items-center mb-1">
            <h3 
              className="text-sm font-medium"
              style={{ color: config['dark-color'] }}
            >
              {getTranslatedText('add_to_calendar') || 'Add to Calendar'}
            </h3>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={16} />
            </button>
          </div>
          
          <div className="space-y-2">
            <button
              onClick={() => handleCalendarClick('google')}
              className="w-full flex items-center p-3 border rounded-lg hover:bg-gray-50 transition-colors"
              style={{ borderRadius: config.border_radius }}
            >
              <div className="w-8 h-8 mr-3 flex-shrink-0">
                <GoogleCalendarIcon />
              </div>
              <div className="flex-1 text-sm font-medium">Google Calendar</div>
            </button>
            
            <button
              onClick={() => handleCalendarClick('apple')}
              className="w-full flex items-center p-3 border rounded-lg hover:bg-gray-50 transition-colors"
              style={{ borderRadius: config.border_radius }}
            >
              <div className="w-8 h-8 mr-3 flex-shrink-0">
                <AppleCalendarIcon />
              </div>
              <div className="flex-1 text-sm font-medium">Apple Calendar</div>
            </button>
            
            <button
              onClick={() => handleCalendarClick('outlook')}
              className="w-full flex items-center p-3 border rounded-lg hover:bg-gray-50 transition-colors"
              style={{ borderRadius: config.border_radius }}
            >
              <div className="w-8 h-8 mr-3 flex-shrink-0">
                <OutlookCalendarIcon />
              </div>
              <div className="flex-1 text-sm font-medium">Outlook</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};